import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  role?: 'user' | 'admin';
  avatarUrl: string | null;
  plan: 'free' | 'starter' | 'pro' | 'expert' | 'ultra';
  units: 'metric' | 'imperial';
  dateFormat: string;
  timeFormat: '12h' | '24h';
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  subscriptionStatus?: string | null;
  cancelAtPeriodEnd?: boolean | null;
  currentPeriodEnd?: string | null;
  impersonatorId?: string;
  impersonator?: {
    id: string;
    email: string;
    name: string;
  } | null;
}

interface AuthState {
  token: string | null;
  user: UserProfile | null;
  isAuthenticated: boolean;
}

// Decode JWT token to extract payload
function decodeJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

const getInitialAuthState = (): AuthState => {
  if (typeof window === 'undefined') {
    return { token: null, user: null, isAuthenticated: false };
  }

  // Handle Loopback token from URL query params (e.g. for renderer mode)
  const urlParams = new URLSearchParams(window.location.search);
  const queryToken = urlParams.get('token');

  if (queryToken) {
    localStorage.setItem('token', queryToken);
    const decoded = decodeJwt(queryToken);
    const mockUser: UserProfile = decoded
      ? {
          id: decoded.sub,
          email: decoded.email || 'renderer@system.internal',
          name: decoded.name || 'System Renderer',
          avatarUrl: decoded.avatarUrl || null,
          plan: decoded.plan || 'ultra',
          units: decoded.units || 'metric',
          dateFormat: decoded.dateFormat || 'YYYY-MM-DD',
          timeFormat: decoded.timeFormat || '24h',
        }
      : {
          id: 'puppeteer-renderer',
          email: 'renderer@system.internal',
          name: 'System Renderer',
          avatarUrl: null,
          plan: 'ultra',
          units: 'metric',
          dateFormat: 'YYYY-MM-DD',
          timeFormat: '24h',
        };
    localStorage.setItem('user', JSON.stringify(mockUser));
    return {
      token: queryToken,
      user: mockUser,
      isAuthenticated: true,
    };
  }

  const savedToken = localStorage.getItem('token');
  const savedUserJson = localStorage.getItem('user');
  let savedUser: UserProfile | null = null;
  try {
    savedUser = savedUserJson ? JSON.parse(savedUserJson) : null;
  } catch {}

  return {
    token: savedToken,
    user: savedUser,
    isAuthenticated: !!savedToken,
  };
};

const initialState: AuthState = getInitialAuthState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ token: string; user?: UserProfile }>) => {
      const decoded = decodeJwt(action.payload.token);
      const userProfile: UserProfile = action.payload.user || {
        id: decoded?.sub || '',
        email: decoded?.email || '',
        name: decoded?.name || '',
        avatarUrl: decoded?.avatarUrl || null,
        plan: decoded?.plan || 'free',
        units: decoded?.units || 'metric',
        dateFormat: decoded?.dateFormat || 'YYYY-MM-DD',
        timeFormat: decoded?.timeFormat || '24h',
        impersonatorId: decoded?.impersonatorId,
        impersonator: decoded?.impersonator || null,
      };

      if (!userProfile.impersonatorId && decoded?.impersonatorId) {
        userProfile.impersonatorId = decoded.impersonatorId;
        userProfile.impersonator = decoded.impersonator;
      }

      state.token = action.payload.token;
      state.user = userProfile;
      state.isAuthenticated = true;

      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('user', JSON.stringify(userProfile));
    },
    updateUser: (state, action: PayloadAction<UserProfile>) => {
      state.user = action.payload;
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    logout: (state) => {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;

      localStorage.removeItem('token');
      localStorage.removeItem('user');
    },
  },
});

export const { setCredentials, updateUser, logout } = authSlice.actions;
export default authSlice.reducer;
